import { Injectable, Injector, OnDestroy } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpParams,
  HttpParameterCodec
} from '@angular/common/http';
import { Store } from '@ngrx/store';
import { sm3, sm4 } from 'sm-crypto';
import { trim } from 'lodash';
import { encrypt } from '@shared/util/sm2';
import { Observable, Subject } from 'rxjs';
import { environment } from '@env/environment';
import { filter, takeUntil } from 'rxjs/operators';
import { BasicConfigService } from '@core/service/basicConfig.service';
import { keyMap } from './interceptor.type';

class CustomURLEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }
  encodeValue(key: string): string {
    return encodeURIComponent(key);
  }
  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }
  decodeValue(key: string) {
    return decodeURIComponent(key);
  }
}

@Injectable()
export class RequestInterceptor implements HttpInterceptor, OnDestroy {
  private get configService(): BasicConfigService {
    return this.injector.get(BasicConfigService);
  }

  private destroySub$: Subject<void> = new Subject<void>();

  constructor(private injector: Injector) {}

  ngOnDestroy(): void {
    this.destroySub$.next();
    this.destroySub$.complete();
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // 是否需要加密
    const isGlobalEncrypt: boolean = this.configService.get('isGlobalEncrypt') || false;

    let url: string = req.url;
    const needRenameUrl = !url.startsWith('https://') && !url.startsWith('http://') && !url.startsWith('assets');
    if (needRenameUrl) {
      url = environment.SERVER_URL + url;
    }
    let newParams = new HttpParams();
    const params = req.params;
    const keys = params.keys();

    // 由于http默认过滤空数组导致对应value会变为undefined需做特殊处理
    keys.forEach(key => {
      const value = params.get(key);
      if (value !== undefined) newParams = newParams.append(key, encodeURIComponent(value));
    });

    let newReq;
    let paramsNeedEncrypt: boolean; // 请求参数加密
    let xencio_resp_encrypt_only: boolean; // GET的无参数情况、POST的文件上传情况
    const isFileDownload: boolean = ['arraybuffer', 'blob'].includes(req.responseType.toLocaleLowerCase());
    const xencio_trace_id: string = `${this.generateUUID()}_${new Date().getTime()}`; // uuid_timestamp

    if (typeof isGlobalEncrypt === 'boolean' && isGlobalEncrypt && needRenameUrl) {
      const loginEnv: string = this.configService.get('loginEnv') || 'other';
      const serverPublicKey: string = keyMap[loginEnv].pub;
      const key: string = this.generateSM4Key(); // sm4加密用的KEY
      let xencio_hash: string = ''; // 验签信息，+表示字符串拼接
      paramsNeedEncrypt = false;
      xencio_resp_encrypt_only = false; // GET的无参数情况、POST的文件上传情况
      if (keys.length > 0) {
        paramsNeedEncrypt = true;
      } else if (req.method === 'GET') {
        xencio_resp_encrypt_only = true;
      }
      let queryString: string = keys.length > 0 ? sm4.encrypt(newParams.toString(), key) : '';
      let updates: any = {
        url,
        withCredentials: true,
        headers: req.headers
          .set('Cache-Control', 'no-cache')
          .set('Pragma', 'no-cache')
          .set('Expires', 'Sat, 01 Jan 2000 00:00:00 GMT')
          .set('If-Modified-Since', '0'),
        params: queryString
      };
      newReq = req.clone(updates);
      const urlLength: number = newReq.urlWithParams.length;
      // 加密以后导致url的长度过长，GET请求转POST请求
      if (urlLength >= 2083) {
        updates.method = 'POST';
        updates.headers = updates.headers.set('xencio-method', 'GET');
        updates.body = queryString;
        updates.params = new HttpParams();
        newReq = newReq.clone(updates);
        paramsNeedEncrypt = true;
      }
      const body = req.body;
      if (body && body.updates) {
        let formData = new HttpParams({ encoder: new CustomURLEncoder() });
        body.updates.forEach(paramObj => {
          formData = formData.append(
            paramObj.param,
            typeof paramObj.value === 'string' ? trim(paramObj.value) : paramObj.value
          );
        });
        queryString = sm4.encrypt(formData.toString(), key);
        paramsNeedEncrypt = true;
        updates.headers = updates.headers
          .set('Content-Type', 'application/json')
          .set('xencio-content', 'multipart/form-data');
        newReq = newReq.clone({ body: queryString, url });
      } else if (body instanceof FormData) {
        const needEncryptFormData = [...(body as any).values()].every(val => !(val instanceof File));
        if (needEncryptFormData) {
          let formDataString: string = '';
          [...(body as any).keys()].forEach(key => {
            formDataString += `${key}=${body.get(key)}&`;
          });
          formDataString = formDataString.slice(0, -1);
          queryString = sm4.encrypt(formDataString, key);
          paramsNeedEncrypt = true;
          newReq = newReq.clone({ body: queryString, url });
          updates.headers = updates.headers
            .set('Content-Type', 'application/json')
            .set('xencio-content', 'multipart/form-data');
        } else {
          xencio_resp_encrypt_only = true;
        }
      } else if (req.method === 'POST' && this.isJson(body)) {
        const paramsString: string = JSON.stringify(body);
        paramsString.length ? (paramsNeedEncrypt = true) : (xencio_resp_encrypt_only = true);
        queryString = sm4.encrypt(paramsString, key);
        newReq = newReq.clone({ body: queryString, url });
        updates.headers = updates.headers.set('Content-Type', 'application/json');
      }

      if (paramsNeedEncrypt || xencio_resp_encrypt_only) {
        updates.headers = updates.headers.set('xencio-need-decrypt', 'true');
        xencio_hash = `${sm3(queryString + xencio_trace_id)}`;
        if (paramsNeedEncrypt) {
          const xencio_key: string = encrypt(key, serverPublicKey);
          updates.headers = updates.headers.set('xencio-key', xencio_key).set('xencio-hash', xencio_hash);
        }
        if (xencio_resp_encrypt_only) {
          updates.headers = updates.headers.set('xencio-hash', xencio_hash).set('xencio-resp-encrypt-only', 'true');
        }
        if (!isFileDownload) {
          updates.responseType = 'text';
        }
      }
      updates.headers = updates.headers.set('xencio-trace-id', xencio_trace_id);
      newReq = newReq.clone(updates);
    } else {
      newReq = req.clone({
        url,
        withCredentials: true,
        headers: req.headers
          .set('Cache-Control', 'no-cache')
          .set('Pragma', 'no-cache')
          .set('Expires', 'Sat, 01 Jan 2000 00:00:00 GMT')
          .set('If-Modified-Since', '0')
          .set('xencio-trace-id', xencio_trace_id)
      });
      const body = req.body;
      if (body && body.updates) {
        let formData = new HttpParams({ encoder: new CustomURLEncoder() });
        body.updates.forEach(paramObj => {
          formData = formData.append(
            paramObj.param,
            typeof paramObj.value === 'string' ? trim(paramObj.value) : paramObj.value
          );
        });
        newReq = newReq.clone({ body: formData });
      }
    }
    return next.handle(newReq);
  }

  // 生成一个随机的SM4 key
  generateSM4Key(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    let hexString: string = '';
    for (let i = 0; i < array.length; i++) {
      hexString += `0${array[i].toString(16)}`.slice(-2);
    }
    return hexString;
  }

  // 生成UUID
  generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c: string) => {
      const randomNum: number = (Math.random() * 16) | 0,
        value: number = c === 'x' ? randomNum : (randomNum & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  // 判断是不json格式
  isJson(obj: any): boolean {
    try {
      JSON.parse(JSON.stringify(obj));
      return true;
    } catch (e) {
      return false;
    }
  }
}
